import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SimplePage } from "@/components/SimplePage";
import { useAuth, isMember } from "@/lib/auth";
import { joinFamily, errorText } from "@/lib/family";

export default function JoinFamily() {
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState(params.get("code") || localStorage.getItem("pp_join_code") || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (code.trim()) localStorage.setItem("pp_join_code", code.trim()); }, [code]);

  const join = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await joinFamily(code);
      localStorage.removeItem("pp_join_code");
      localStorage.removeItem("pp_profile");
      await refreshUser();
      toast.success(`Welcome to the family! You joined as ${r.role === "grandparent" ? "a grandparent" : "a parent"}.`);
      nav("/family");
    } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  const cancel = () => { localStorage.removeItem("pp_join_code"); nav("/"); };

  return (
    <SimplePage testId="join-family-page">
      <p className="pp-label">Family invite</p>
      <h1 className="pp-h1">Join your family on NICE</h1>
      <p className="pp-sub max-w-xl">See the children's progress, give golden acorns, and practice manners together. Each adult uses their own NICE account and membership.</p>
      {user === null ? <Loader2 className="h-6 w-6 animate-spin mt-6" /> : !user ? (
        <div className="pp-actions" data-testid="join-need-account">
          <Link to="/signup" className="pp-primary" data-testid="join-signup-link">Create your account</Link>
          <Link to="/login" className="pp-ghost" data-testid="join-login-link">I already have an account</Link>
        </div>
      ) : !isMember(user) ? (
        <div className="pp-actions" data-testid="join-need-membership">
          <p className="w-full">Start your membership first, then you'll come right back here to join.</p>
          <Link to="/subscribe" className="pp-primary" data-testid="join-subscribe-link">Start membership</Link>
        </div>
      ) : (
        <form onSubmit={join} className="pp-card max-w-md mt-6 grid gap-2" data-testid="join-form">
          <label className="pp-label" htmlFor="join-code">Invite code</label>
          <input id="join-code" className="pp-input" style={{ marginTop: 0 }} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. 7F3A9C21" maxLength={16} data-testid="join-code-input" />
          <p className="text-xs opacity-70">Joining switches this device to the family you join.</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <button className="pp-primary" disabled={busy || !code.trim()} data-testid="join-submit"><Users className="h-5 w-5" /> Join family</button>
            <button type="button" className="pp-ghost" onClick={cancel} data-testid="join-cancel">Not now</button>
          </div>
        </form>
      )}
    </SimplePage>
  );
}
